import {
  FeeEstimationFailedError,
  InvalidParamError,
  SimulationFailedError,
  getDefaultClient,
  getNetworksData,
} from '@dynamic-labs-sdk/client';
import { createApiClient, getCore } from '@dynamic-labs-sdk/client/core';
import type { SimulateEVMTransactionRequest } from '@dynamic-labs/sdk-api-core';

import type { EvmWalletAccount } from '../EvmWalletAccount.types';
import type {
  EvmSimulationResult,
  EvmTransactionToSimulate,
} from '../transactionSimulation.types';
import { calculateEvmTransactionFee } from '../utils/calculateEvmTransactionFee';
import { isEvmWalletAccount } from '../utils/isEvmWalletAccount';

type SimulateEvmTransactionParams = {
  includeFees?: boolean;
  transaction: EvmTransactionToSimulate;
  walletAccount: EvmWalletAccount;
};

/**
 * Simulates an EVM transaction to preview its effects before execution.
 *
 * This function uses the Blockaid API to analyze the transaction and returns
 * information about asset transfers, security validation, and optionally fee estimates.
 * The simulation shows what will happen when the transaction is executed without
 * actually sending it to the blockchain.
 *
 * @param params.walletAccount - The EVM wallet account that will sign the transaction.
 * @param params.transaction - The transaction to simulate, including network ID.
 * @param params.includeFees - Whether to calculate transaction fees (default: false).
 * @param [client] - The Dynamic client instance. Only required when using multiple Dynamic clients.
 * @returns Simulation result with asset diffs, security validation, and optional fee data.
 * @throws {InvalidParamError} If wallet account is not an EVM account or network ID is not configured.
 * @throws {SimulationFailedError} If the simulation API call fails.
 * @throws {FeeEstimationFailedError} If includeFees is true and fee calculation fails.
 */
export const simulateEvmTransaction = async (
  {
    walletAccount,
    transaction,
    includeFees = false,
  }: SimulateEvmTransactionParams,
  client = getDefaultClient()
): Promise<EvmSimulationResult> => {
  // Validate wallet account type
  if (!isEvmWalletAccount(walletAccount)) {
    throw new InvalidParamError('Wallet account must be an EVM wallet account');
  }

  // Get network data for the specified network
  const networksData = getNetworksData(client);
  const networkData = networksData.find(
    (data) => data.networkId === transaction.networkId && data.chain === 'EVM'
  );

  if (!networkData) {
    throw new InvalidParamError(
      `Network with ID ${transaction.networkId} is not configured in project settings`
    );
  }

  try {
    // Create API client and call simulation endpoint
    const core = getCore(client);
    const apiClient = createApiClient({}, client);

    const simulateEVMTransactionRequest = {
      chainId: transaction.networkId,
      data: transaction.data || '0x',
      from: transaction.from,
      to: transaction.to,
    } as SimulateEVMTransactionRequest;

    if (transaction.value !== undefined) {
      simulateEVMTransactionRequest.value = transaction.value.toString();
    }

    const response = await apiClient.simulateEVMTransaction({
      environmentId: core.environmentId,
      simulateEVMTransactionRequest,
    });

    // Calculate fees if requested
    let feeData: EvmSimulationResult['feeData'];

    if (includeFees) {
      feeData = await calculateEvmTransactionFee({
        nativeTokenPriceUsd: response.priceData?.nativeTokenUsdPrice,
        networkData,
        transaction,
      });
    }

    // Map API response to simulation result
    return {
      counterparties: response.counterparties,
      feeData,
      inAssets: response.inAssets || [],
      outAssets: response.outAssets || [],
      priceData: response.priceData,
      showTotalFiat: response.showTotalFiat,
      validation: response.validation
        ? {
            description: response.validation.description,
            reason: response.validation.reason,
            result: response.validation.result as
              | 'benign'
              | 'warning'
              | 'malicious',
          }
        : undefined,
    };
  } catch (error) {
    // Re-throw FeeEstimationFailedError as-is
    if (error instanceof FeeEstimationFailedError) {
      throw error;
    }

    throw new SimulationFailedError({
      cause: error instanceof Error ? error : undefined,
      message: 'Failed to simulate EVM transaction',
    });
  }
};
